"use client";

import {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type Ref,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  isArray,
  isBoolean,
  isEmpty,
  isString,
  mergeClasses,
} from "@huub-web/utils";

import { Text } from "../Text";
import { Icon } from "../Icon";
import {
  SelectList,
  type SelectListOption,
  type SelectListValue,
} from "../SelectList";
import { Popup, type PopupProps } from "../Popup";
import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import { inputStyle } from "../BaseInputField/BaseInputField.css";
import { typography } from "../../theme/css/typography.css";
import { mergeRefs } from "../../utils/mergeRefs";
import { activeOptionStyle, iconStyle } from "./Select.css";
import { useClickOutside } from "../../hooks/useClickOutside";
import { Edges } from "../Edges";
import { useFormValidation } from "../../hooks/useFormValidation";
import type { ValidatableProps } from "../../props/ValidatableProps";
import type { FieldMetadata } from "../../contexts/FormContext";

export type SelectProps = Omit<BaseInputFieldProps, "defaultValue"> &
  ValidatableProps & {
    value?: SelectListValue;
    defaultValue?: SelectListValue;
    popup?: Pick<PopupProps, "placement" | "minWidth" | "maxWidth" | "width">;
    options?: SelectListOption[];
    inputRef?: Ref<HTMLInputElement>;
    placeholder?: string;
    multi?: boolean;
    search?: boolean;
    onChange?: SelectChangeHandler;
    onOutsideClick?: () => void;
    /**
     * The backend field key for this input (e.g. "applicant.loanType").
     * Passed by FormRow via cloneElement from the data-field-key attribute.
     * Forwarded to useFormValidation for AI agent field awareness.
     */
    fieldKey?: string;
  };

export type SelectChangeHandler<T = SelectListValue> = (value: T) => void;

/**
 * Select component is a dropdown component that allows users to select a single option
 * from a list of options.
 */
export const Select: FC<SelectProps> = ({
  id: idProp,
  error: errorProp,
  disabled: disabledProp,
  placeholder,
  inputRef,
  value,
  defaultValue,
  options = [],
  display,
  popup,
  variant = "default",
  size = "default",
  autoFocus = false,
  multi = false,
  search = false,
  validation,
  name,
  onChange,
  onClick,
  onOutsideClick,
  className,
  label,
  fieldKey,
  ref,
  ...props
}) => {
  const proxyRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const [isActive, setIsActive] = useState(autoFocus);

  const metadata = useMemo<FieldMetadata>(
    () => ({
      label: isString(label) ? label : undefined,
      type: "select",
      options: options?.map((o) => ({
        value: String(o.value ?? ""),
        label: typeof o.label === "string" ? o.label : String(o.value ?? ""),
      })),
      required: validation?.includes("required"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [label, options, validation],
  );
  const { id, disabled, error } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: proxyRef,
    fieldKey,
    metadata,
  });

  useClickOutside<HTMLDivElement>([popupRef, fieldRef], () => {
    closePopup();
    onOutsideClick?.();
  });

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    onClick?.(e);
    openPopup();
  };

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setIsActive(true);
  };

  const handleClose = () => {
    closePopup();
  };

  const closePopup = () => {
    if (isActive && proxyRef.current) {
      proxyRef.current.focus();
    }
    setIsActive(false);
  };

  const handleSelect = (option: SelectListOption) => {
    if (multi) {
      const newValue = isArray(value) ? [...value] : [];

      let index = newValue.indexOf(option.value as string | number);
      if (index === -1) {
        index = newValue.indexOf(option.value.toString());
      }
      if (index === -1) {
        index = newValue.map(String).indexOf(option.value.toString());
      }
      if (index === -1) {
        newValue.push(option.value as string | number);
      } else {
        newValue.splice(index, 1);
      }

      onChange?.(newValue);
    } else {
      onChange?.(option.value);
      closePopup();
    }
  };

  const handleProxyChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    console.warn("handleInputChange", e.target.value);
  };
  const handleProxyKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    // console.log("[Select] handleProxyKeyDown", e.key);

    if (e.key === "Enter" || e.key === " ") {
      openPopup();
    } else if (e.key === "Tab") {
      return;
    }

    e?.preventDefault();
  };

  const activeOption: SelectListOption[] | SelectListOption | undefined =
    (() => {
      if (multi) {
        return options.filter((option) =>
          isArray(value)
            ? value.includes(option.value as string | number) ||
              value.map(String).includes(option.value.toString())
            : false,
        );
      }
      const option = options?.find(
        (option) =>
          option.value === value ||
          option.value.toString() === value?.toString(),
      );
      return option ? option : undefined;
    })();

  return (
    <Popup
      ref={popupRef}
      active={isActive}
      onClose={handleClose}
      content={
        <SelectList
          multi={multi}
          value={value}
          search={search}
          options={options}
          onSelect={handleSelect}
        />
      }
      variant={variant === "toggle" ? "toggle" : undefined}
      applyActive
      trapFocus
      escape
      size="minimal"
      {...popup}
      placement={popup?.placement ?? "bottomRight"}
    >
      <BaseInputField
        // (!) [ref] is being overruled by popup
        altRef={mergeRefs(ref, fieldRef)}
        onClick={handleClick}
        disabled={disabled}
        width="stretch"
        label={label}
        labelFloat={isEmpty(value) && !isActive && !placeholder}
        trailingLast={
          props.trailingLast ??
          (variant === "default" || variant === "opaque" ? (
            <Icon
              className={iconStyle({ isOpen: isActive })}
              icon="chevronDown"
              size="s2"
            />
          ) : undefined)
        }
        placeholder={
          isEmpty(value) && placeholder && !display ? placeholder : undefined
        }
        display={display}
        variant={variant}
        size={size}
        error={error}
        proxy={
          <input
            id={id}
            ref={mergeRefs(proxyRef, inputRef)}
            className={mergeClasses(
              typography.bodyS,
              inputStyle({ proxy: true, disabled }),
            )}
            disabled={disabled}
            value={
              isBoolean(value)
                ? value
                  ? 1
                  : 0
                : isArray(value)
                  ? value.join(",")
                  : (value ?? "")
            }
            defaultValue={
              isBoolean(defaultValue)
                ? defaultValue
                  ? 1
                  : 0
                : isArray(defaultValue)
                  ? defaultValue.join(",")
                  : defaultValue
            }
            onKeyDown={handleProxyKeyDown}
            onChange={handleProxyChange}
            autoComplete="off"
            data-1p-ignore
          />
        }
        className={className}
        {...props}
      >
        <div>
          {!multi && activeOption && !isArray(activeOption) && (
            <div className={activeOptionStyle()}>
              <Edges
                leading={activeOption.leading}
                trailing={activeOption.trailing}
              >
                <Text
                  variant={variant === "toggle" ? "bodyL" : "bodyM"}
                  ellipsis
                >
                  {activeOption.label}
                </Text>
              </Edges>
            </div>
          )}

          {multi && activeOption && isArray(activeOption) && (
            <div className={activeOptionStyle()}>
              <Text variant={variant === "toggle" ? "bodyL" : "bodyM"} ellipsis>
                {activeOption.map((option) => option.label).join(", ")}
              </Text>
            </div>
          )}
        </div>
      </BaseInputField>
    </Popup>
  );
};
